import remove from 'lodash/remove'
import { notification } from 'antd'
import { canonizeString, request, generalErrorHandler, filterSectorsAndSetRootSectorOfInstitution } from '../../utils'
import appConfig from '../../config'
import { getTranslate } from 'react-localize-redux'

const SET_INSTITUTIONS = 'UDPATE_INSTITUTIONS'
const SET_INSTITUTION_TIME_PRESETS = 'SET_INSTITUTION_TIME_PRESETS'
const SET_MULTI_INSTITUTIONS_PRESETS = 'SET_MULTI_INSTITUTIONS_PRESETS'
const REMOVE_INSTITUTION_USER = 'REMOVE_INSTITUTION_USER'
const CLEAR_MULTI_INSTITUTIONS_PRESETS = 'CLEAR_MULTI_INSTITUTIONS_PRESETS'
const SET_INSTITUTION_OPTIONS_BY_ID = 'SET_INSTITUTION_OPTIONS_BY_ID'

export const getInstitutions = state => {
  return state.getInstitutions.list
}

export const setInstitutions = institutions => {
  institutions = institutions.sort((i1, i2) => {
    return canonizeString(i1.name).localeCompare(canonizeString(i2.name))
  })
  institutions = institutions.map(i => {
    if (i.sectors) {
      i.sectors = i.sectors.sort((s1, s2) => {
        return canonizeString(s1.name).localeCompare(canonizeString(s2.name))
      })
    } else {
      i.sectors = []
    }
    return i
  })

  return {
    type: SET_INSTITUTIONS,
    institutions: institutions
  }
}

export const fetchInstitutions = (currentUser) => {
  return async (dispatch, getState) => {
    try {
      let institutionsList = []
      const url = `/institutions/0/${appConfig.apiPageSize}`
      const { data, nextPage } = await request(url, 'GET', null, currentUser)
      let nextPageInfo = nextPage
      institutionsList.push(...data)
      while (nextPageInfo) {
        const newPageResults = await request(nextPageInfo, 'GET', null, currentUser)
        if (newPageResults.data) {
          institutionsList.push(...newPageResults.data)
        }
        nextPageInfo = newPageResults.nextPage
      }

      if (institutionsList.length) {
        institutionsList = institutionsList.sort((i1, i2) => {
          return canonizeString(i1.name).localeCompare(canonizeString(i2.name))
        })
        institutionsList = institutionsList.map(i => {
          i.sectors = i.sectors.sort((s1, s2) => {
            return canonizeString(s1.name).localeCompare(canonizeString(s2.name))
          })

          return i
        })

        // find the root sector and translate its name
        const translationMethod = getTranslate(getState().locale)

        // set the root sector
        filterSectorsAndSetRootSectorOfInstitution(
          institutionsList[0],
          translationMethod('Without care unit')
        )

        await dispatch({
          type: SET_INSTITUTIONS,
          institutions: institutionsList
        })

        return institutionsList
      }
    } catch (err) {
      generalErrorHandler(err)
      return null
    }
  }
}

export const saveQuotasDisplayOption = (currentUser, institutionId, quotasPerSector = false) => {
  return async (dispatch, getState) => {
    const response = await request(
      `/api/institutions/${institutionId}/quotas-display`,
      'POST',
      { quotasPerSector },
      currentUser
    )

    if (response.data) {
      const translationMethod = getTranslate(getState().locale)

      notification.success({
        message: translationMethod('Recorded'),
        placement: 'bottomRight'
      })

      dispatch(setInstitutionOptions(institutionId, response.data.institutionOptions))
    }
  }
}

export const setInstitutionTimePresets = ({
  institutionId,
  sectorId,
  presetsList
}) => {
  return {
    type: SET_INSTITUTION_TIME_PRESETS,
    payload: { institutionId, sectorId, presetsList }
  }
}

export const removeInstitutionUser = ({
  institutionId,
  userId,
  currentUser
}) => {
  request(`/api/User/${userId}`, 'DELETE', null, currentUser)
  return {
    type: REMOVE_INSTITUTION_USER,
    payload: { institutionId, userId }
  }
}

/**
 * This method fetches the presets for all the institutions specified
 * institutionIds is an array of ids
 */
export const fetchInstitutionPresets = ({ institutionIds, currentUser, forWidget }) => {
  return async (dispatch) => {
    try {
      const { data } = await request('/shift/presets/institutions', 'POST', { institutions: institutionIds }, currentUser)
      if (data.status === undefined) {
        const multiInstitutionsPresets = []
        for (const institutionId in data) {
          const institutionTimePresets = [] // this will contain items with properties {sectorId, presetsList}

          const { presets, deletedDefaultPresets } = data[institutionId]
          for (const key in presets) {
            const presetsList = []
            presets[key].forEach(preset => {
              let canAddToPresetsList = true
              if (preset.isDefault) {
              // check if the default preset was previously removed
                if (deletedDefaultPresets[key] !== undefined && deletedDefaultPresets[key].find(item => item.periodCode === preset.periodCode) !== undefined) {
                  canAddToPresetsList = false
                }
              }
              if (canAddToPresetsList) {
                presetsList.push({
                  id: preset.id,
                  periodCode: preset.periodCode,
                  startTime: preset.startTime,
                  endTime: preset.endTime,
                  pause: preset.pause ? +preset.pause : 0,
                  isDefault: preset.isDefault,
                  color: preset.color || null,
                  shiftPause: preset.shiftPause
                })
              }
            })

            institutionTimePresets.push({
              sectorId: +key,
              presetsList
            })
          }

          multiInstitutionsPresets.push({
            institutionId: +institutionId,
            presets: institutionTimePresets
          })
        }

        if (forWidget) {
          /**
           * in this case we don't have institutions loaded in our state so we create a light version
           * which contains only the presets for each institution
           * This is needed to have colors on the shifts
           */
          const institutions = multiInstitutionsPresets.map(item => ({
            id: item.institutionId,
            name: '',
            sectors: item.presets.map(sectorDetails => ({ id: sectorDetails.sectorId, name: '', presets: sectorDetails.presetsList }))
          }))

          dispatch({
            type: SET_INSTITUTIONS,
            institutions
          })
        } else {
          dispatch({
            type: SET_MULTI_INSTITUTIONS_PRESETS,
            payload: multiInstitutionsPresets
          })
        }
        return multiInstitutionsPresets
      }
      return null
    } catch (err) {
      generalErrorHandler(err)
      return null
    }
  }
}

export const clearInstitutionsPresets = () => {
  return {
    type: CLEAR_MULTI_INSTITUTIONS_PRESETS
  }
}

/**
 * This is the actual method which updates the list of presets for each section
 */
const updatePresetsList = ({ state, institutionId, sectorId, presetsList }) => {
  return state.list.map(institution => {
    if (institution.id === institutionId) {
      institution.sectors = institution.sectors.map(sector => {
        if (sector.id === sectorId) {
          return { ...sector, presets: presetsList }
        }
        return sector
      })
      return institution
    }
    return institution
  })
}

const updateMultipleInstitutionsPresetsList = ({
  state,
  institutionsPresetsList
}) => {
  return state.list.map(institution => {
    const presetsData = institutionsPresetsList.find(item => item.institutionId === institution.id)
    if (presetsData) {
      institution.sectors = institution.sectors.map(sector => {
        const sectorWithTimePreset = presetsData.presets.find(
          item => item.sectorId === sector.id
        )
        if (sectorWithTimePreset) {
          return { ...sector, presets: sectorWithTimePreset.presetsList }
        }
        return sector
      })
      return institution
    }
    return institution
  })
}

const handleUserRemove = ({ state, institutionId, userId }) => {
  return state.list.map(institution => {
    if (institution.id === institutionId) {
      remove(institution.users, item => item.id === userId)
    }
    return institution
  })
}

const handleClearInstitutionsPresets = state => {
  return state.list.map(institution => {
    institution.sectors = institution.sectors.map(sector => {
      if (sector.presets) {
        const { presets, ...otherSectorProps } = sector
        return { ...otherSectorProps }
      }
      return sector
    })
    return institution
  })
}

const setInstitutionOptions = (id, options) => {
  return {
    type: SET_INSTITUTION_OPTIONS_BY_ID,
    payload: { id, options }
  }
}

export default (state = { list: [] }, action) => {
  switch (action.type) {
    case SET_INSTITUTIONS: {
      return { ...state, list: action.institutions }
    }
    case SET_INSTITUTION_TIME_PRESETS:
      return { ...state, list: updatePresetsList({ ...action.payload, state }) }
    case SET_MULTI_INSTITUTIONS_PRESETS:
      return { ...state, list: updateMultipleInstitutionsPresetsList({ institutionsPresetsList: action.payload, state }) }
    case REMOVE_INSTITUTION_USER:
      return { ...state, list: handleUserRemove({ ...action.payload, state }) }
    case CLEAR_MULTI_INSTITUTIONS_PRESETS:
      return { ...state, list: handleClearInstitutionsPresets(state) }
    case SET_INSTITUTION_OPTIONS_BY_ID: {
      let institutions = [...state.list]

      institutions = institutions.map(institution => {
        if (institution.id === action.payload.id) {
          institution.institutionOptions = action.payload.options
        }

        return institution
      })

      return { ...state, list: institutions }
    }
    default:
      return state
  }
}
